import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import {
  applyDarkModeToDocument,
  persistDarkModePreference,
  readDarkModePreference,
} from "utils/theme";

interface ThemeContextType {
  isDark: boolean;
  toggle: () => void;
  /** @deprecated Use isDark — kept for existing callers */
  theme: "light" | "dark";
  /** @deprecated Use toggle — kept for existing callers */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const [isDark, setIsDark] = useState<boolean>(readDarkModePreference);

  useEffect(() => {
    applyDarkModeToDocument(isDark, pathname);
    persistDarkModePreference(isDark);
  }, [isDark, pathname]);

  const toggle = () => setIsDark((prev) => !prev);

  const value: ThemeContextType = {
    isDark,
    toggle,
    theme: isDark ? "dark" : "light",
    toggleTheme: toggle,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
